/**
 * The footer that carries the four site links, on all four pages.
 *
 * Authorised by `docs/marketing/app-store-listing.md` §URLs, which requires a Support
 * URL and a Privacy Policy URL and names `apps/web` as the host of both. A required URL
 * that can only be reached by typing it is a URL nobody finds, and App Review follows
 * links. `/terms` is the founder-directed fourth page (`docs/DEVIATIONS.md` **W3**) and
 * belongs here for exactly the same reason.
 *
 * design-direction §4 ("spatial grammar is fixed. No hamburger roulette, no smart
 * rearrangement, no reordering"): the same four links, in the same order, in the same
 * place on every page. The current page is marked with `aria-current` and is not a link,
 * so the answer to "where am I" is in the markup and not only in the address bar.
 *
 * TERMS IS APPENDED, NOT INSERTED. Grouping it beside Privacy would read better and
 * would move Support, which had already been in third position on a shipped page — and
 * §4's rule against rearrangement is about exactly that. A worse grouping costs a reader
 * one extra glance once; a moved link costs every returning reader the position they
 * learned.
 *
 * Touch targets are `space.minTarget` tall — design-direction's ≥56px law applies to a
 * browser on a phone exactly as it does in the app. Four targets at that height wrap on a
 * narrow phone rather than shrinking, which is what `flexWrap` below is for.
 */
import type { CopyKey } from '@carn/copy';
import { useCopy } from '../theme/CopyProvider';
import { useTheme } from '../theme/ThemeProvider';
import { text } from './prose';

export type SitePath = '/' | '/privacy' | '/support' | '/terms';

const LINKS: readonly { href: SitePath; key: CopyKey }[] = [
  { href: '/', key: 'siteNav.about' },
  { href: '/privacy', key: 'siteNav.privacy' },
  { href: '/support', key: 'siteNav.support' },
  { href: '/terms', key: 'siteNav.terms' },
];

export function SiteFooter({ current }: { current: SitePath }) {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  return (
    <nav
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: s.sm,
        borderTop: `${s.hairline}px solid ${c.border}`,
        paddingTop: s.md,
      }}
    >
      {LINKS.map(({ href, key }) => {
        const here = href === current;
        return (
          <a
            key={href}
            href={href}
            // The current page is announced rather than merely styled — colour is never
            // the only carrier of a state (design-direction §2).
            aria-current={here ? 'page' : undefined}
            style={{
              ...text(theme.type.label),
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: s.minTarget,
              padding: `0 ${s.md}px`,
              borderRadius: s.radius.md,
              color: here ? c.text : c.textSecondary,
              background: here ? c.surface : 'transparent',
              textDecoration: here ? 'none' : 'underline',
              fontFamily: theme.type.family,
            }}
          >
            {t(key)}
          </a>
        );
      })}
    </nav>
  );
}
